import { useEffect, useState, useMemo } from 'react';
import { api } from '../services/apiClient';
import './MarketRadarPage.css';

const REFRESH_INTERVAL = 60_000;
const FILTER_TAGS = ['全部', '黄金', '美联储', '通胀', '非农'];

// ─── 快讯筛选（前端复用，与后端 _radarEngine 逻辑一致）────────
const HEADLINE_KEYWORDS = {
  黄金: ['黄金', '金价', '金价格', 'XAU'],
  美联储: ['美联储', 'Fed', '鲍威尔', 'FOMC', '降息', '加息', '利率决议'],
  通胀: ['CPI', 'PPI', 'PCE', '通胀', '通缩', '物价'],
  非农: ['非农', '就业', '失业金', '失业率', 'ADP'],
};

function filterHeadlines(headlines = [], tag = '全部') {
  if (tag === '全部' || !tag) return headlines;
  const keywords = HEADLINE_KEYWORDS[tag];
  if (!keywords) return headlines;
  return headlines.filter((h) => keywords.some((kw) => (h.title || '').includes(kw)));
}

// ─── 格式化数字 ──────────────────────────────────────────────

function fmtPct(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}%`;
}

function fmtPrice(v, decimals = 0) {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function changeClass(v) {
  if (v == null || !Number.isFinite(v)) return 'flat';
  if (v > 0) return 'up';
  if (v < 0) return 'down';
  return 'flat';
}

function statusClass(status) {
  if (!status) return 'flat';
  if (status.includes('↑')) return '';
  if (status.includes('↓')) return 'down';
  return 'flat';
}

function starEmoji(star) {
  if (star >= 3) return '🔴';
  if (star === 2) return '🟡';
  return '🟢';
}

function formatEventTime(timeStr) {
  if (!timeStr) return '';
  // "2026-06-10 20:30:00" → "06/10 20:30"
  const date = timeStr.slice(5, 10).replace('-', '/');
  const time = timeStr.slice(11, 16);
  return `${date} ${time}`;
}

// ─── 资产卡片 ────────────────────────────────────────────────

function AssetCard({ title, icon, data, renderBody }) {
  if (!data) {
    return (
      <div className="mr-asset-card">
        <div className="mr-asset-card-header">
          <span className="mr-asset-card-title">{icon} {title}</span>
        </div>
        <p className="mr-asset-card-null">暂不可用</p>
      </div>
    );
  }
  return (
    <div className="mr-asset-card">
      <div className="mr-asset-card-header">
        <span className="mr-asset-card-title">{icon} {title}</span>
        {data.status && (
          <span className={`mr-asset-card-status ${statusClass(data.status)}`}>
            {data.status}
          </span>
        )}
      </div>
      {renderBody(data)}
    </div>
  );
}

function GoldCardBody({ price, unit, change, changePercent, cnyPerGram }) {
  return (
    <>
      <div className="mr-asset-card-price">${fmtPrice(price, 2)}</div>
      <div className="mr-asset-card-unit">{unit}</div>
      <div className={`mr-asset-card-change ${changeClass(changePercent)}`}>
        {fmtPct(changePercent)}
      </div>
      <div className="mr-asset-card-sub">
        <div className="mr-asset-card-sub-row">
          <span>人民币</span>
          <span className="mr-asset-card-sub-value">¥{fmtPrice(cnyPerGram, 2)}/克</span>
        </div>
      </div>
    </>
  );
}

function CryptoCardBody({ btcPrice, btcChangePercent, btcStatus, fearGreed, fearLabel }) {
  return (
    <>
      <div className="mr-asset-card-price">${fmtPrice(btcPrice, 0)}</div>
      <div className="mr-asset-card-unit">BTC / USD</div>
      <div className={`mr-asset-card-change ${changeClass(btcChangePercent)}`}>
        {fmtPct(btcChangePercent)}
      </div>
      <div className="mr-asset-card-sub">
        <div className="mr-asset-card-sub-row">
          <span>恐惧贪婪</span>
          <span className="mr-asset-card-sub-value">
            {fearGreed != null ? `${fearGreed} ${fearLabel}` : '—'}
          </span>
        </div>
      </div>
    </>
  );
}

function DollarCardBody({ usdCnh, usdCnhChange, status }) {
  return (
    <>
      <div className="mr-asset-card-price">{usdCnh != null ? fmtPrice(usdCnh, 4) : '—'}</div>
      <div className="mr-asset-card-unit">USDCNH 美元/人民币</div>
      <div className={`mr-asset-card-change ${changeClass(usdCnhChange)}`}>
        {fmtPct(usdCnhChange)}
      </div>
      <div className="mr-asset-card-sub">
        <div className="mr-asset-card-sub-row">
          <span>汇率方向</span>
          <span className="mr-asset-card-sub-value">{status || '—'}</span>
        </div>
      </div>
    </>
  );
}

// ─── 主页面 ──────────────────────────────────────────────────

export default function MarketRadarPage() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('全部');

  useEffect(() => {
    let cancelled = false;

    async function loadRadar() {
      setLoading(true);
      setError('');
      try {
        const data = await api.getMarketRadar();
        if (!cancelled) setSnapshot(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '市场雷达数据暂时不可用');
          setSnapshot(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadRadar();
    const timer = setInterval(loadRadar, REFRESH_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  // 前端筛选快讯
  const filteredHeadlines = useMemo(() => {
    if (!snapshot?.headlines) return [];
    return filterHeadlines(snapshot.headlines, activeFilter);
  }, [snapshot?.headlines, activeFilter]);

  const risk = snapshot?.risk;
  const assets = snapshot?.assets;
  const events = snapshot?.events;

  return (
    <main className="pro-page mr-page">
      <div className="pro-page-inner">
        <header className="pro-hero">
          <p className="pro-hero-label">Market Radar</p>
          <h1>宏观市场雷达</h1>
          <span className="pro-hero-desc">
            跨市场宏观概览 — 黄金、美元、加密市场一目了然，帮你判断今日交易环境
          </span>
        </header>

        {error && !snapshot && <div className="mr-error">{error}</div>}

        {loading && !snapshot && (
          <div className="mr-loading">正在加载市场数据…</div>
        )}

        {snapshot && (
          <>
            {/* 风险概览 */}
            {risk && (
              <section className={`mr-risk-card ${risk.level}`}>
                <span className="mr-risk-emoji">{risk.emoji}</span>
                <div className="mr-risk-main">
                  <span className="mr-risk-label">今日风险：{risk.label}</span>
                  <p className="mr-risk-summary">{risk.summary}</p>
                </div>
              </section>
            )}

            {/* 三张资产卡 */}
            {assets && (
              <section className="mr-assets-grid">
                <AssetCard
                  title="黄金"
                  icon="🥇"
                  data={assets.gold}
                  renderBody={(d) => <GoldCardBody {...d} />}
                />
                <AssetCard
                  title="加密市场"
                  icon="₿"
                  data={assets.crypto}
                  renderBody={(d) => <CryptoCardBody {...d} />}
                />
                <AssetCard
                  title="美元"
                  icon="💵"
                  data={assets.dollar}
                  renderBody={(d) => <DollarCardBody {...d} />}
                />
              </section>
            )}

            {/* 重要事件 */}
            <section className="mr-events">
              <h2>📊 本周重要事件</h2>
              {!events || events.length === 0 ? (
                <p className="mr-events-empty">本周暂无重要经济事件</p>
              ) : (
                events.map((e, i) => (
                  <div className="mr-event-item" key={i}>
                    <span className="mr-event-star">{starEmoji(e.star)}</span>
                    <div className="mr-event-body">
                      <div className="mr-event-top">
                        <span className="mr-event-time">{formatEventTime(e.time)}</span>
                        <span className="mr-event-title">{e.title}</span>
                      </div>
                      {(e.previous || e.consensus || e.actual) && (
                        <div className="mr-event-values">
                          {e.previous && <span>前值 <strong>{e.previous}</strong></span>}
                          {e.consensus && <span>预期 <strong>{e.consensus}</strong></span>}
                          {e.actual && <span>实际 <strong>{e.actual}</strong></span>}
                        </div>
                      )}
                      {e.affect && (
                        <div className="mr-event-impact">金十解读：{e.affect}</div>
                      )}
                      {e.impactNote && (
                        <div className="mr-event-impact">{e.impactNote}</div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </section>

            {/* 精选快讯 */}
            <section className="mr-headlines">
              <h2>📰 最新快讯</h2>
              <div className="mr-headlines-filters">
                {FILTER_TAGS.map((tag) => (
                  <button
                    key={tag}
                    className={`mr-filter-btn${activeFilter === tag ? ' active' : ''}`}
                    onClick={() => setActiveFilter(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              {filteredHeadlines.length === 0 ? (
                <p className="mr-headlines-empty">暂无相关快讯</p>
              ) : (
                filteredHeadlines.map((h, i) => (
                  <div
                    className={`mr-headline-item${h.important ? ' important' : ''}`}
                    key={h.id || i}
                  >
                    <span className="mr-headline-time">
                      {h.time ? h.time.slice(11, 16) : ''}
                    </span>
                    <span className="mr-headline-title">{h.title}</span>
                  </div>
                ))
              )}
            </section>

            {/* 刷新时间 */}
            {snapshot.updatedAt && (
              <p className="mr-updated">
                数据更新于 {new Date(snapshot.updatedAt).toLocaleTimeString('zh-CN')} · 每 60 秒自动刷新
              </p>
            )}
          </>
        )}
      </div>
    </main>
  );
}
